/**
 * BatchReengageView — drives the digest-driven multi-template
 * re-engagement ritual. Arrives via deep link from the weekly
 * engagement digest email (`?demo_insights=batch_reengage&template_ids=...`).
 *
 * Mount stage = config: SE picks A/B angles + auto-split toggle + Go.
 * Then fan-out POST /reengage-batch-draft to LLM-generate N drafts
 * (×2 if A/B) in parallel. Each card renders with variant tabs (when
 * A/B mode is on); the SE picks a variant per card or relies on
 * auto-split (alternates A/B by index).
 *
 * No "send all" — each prospect deserves a different recipient
 * email and a final glance from the SE before dispatch.
 */
import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Loader2, ArrowLeft, Send, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import {
  useAnglePresets, useAngleRecommendation, AnglePicker,
  RecommendedPill, variantBadgeCls,
} from './_angles';

export const BatchReengageView = ({ templateIds, onBack }) => {
  const angles = useAnglePresets();
  // No industry context for batch — multiple templates may span
  // industries, so we always ask the recommender for the global pick.
  const rec = useAngleRecommendation(null);
  const [stage, setStage] = useState('config');
  const [angleA, setAngleA] = useState('curiosity');
  const [angleB, setAngleB] = useState('roi');
  const [angleATouched, setAngleATouched] = useState(false);
  const [angleBTouched, setAngleBTouched] = useState(false);
  const [abMode, setAbMode] = useState(true);
  const [autoSplit, setAutoSplit] = useState(true);

  React.useEffect(() => {
    if (!rec?.available) return;
    if (!angleATouched && rec.recommended_a?.angle) setAngleA(rec.recommended_a.angle);
    if (!angleBTouched && rec.recommended_b?.angle) setAngleB(rec.recommended_b.angle);
  }, [rec, angleATouched, angleBTouched]);

  const onChangeA = (v) => { setAngleATouched(true); setAngleA(v); };
  const onChangeB = (v) => { setAngleBTouched(true); setAngleB(v); };

  const [drafts, setDrafts] = useState(null);
  const [recipients, setRecipients] = useState({});

  const generateBatch = async () => {
    if (abMode && angleA === angleB) {
      toast.error('Pick two different angles to A/B test.');
      return;
    }
    setStage('drafting');
    try {
      const payload = {
        items: templateIds.map((id) => ({ template_id: id, context_note: '' })),
        angles: abMode ? [angleA, angleB] : [angleA],
      };
      const res = await api.post('/admin/demo-director/reengage-batch-draft', payload);
      const enriched = (res.data?.drafts || []).map((d, i) => {
        const initialVariant = (
          d.variants && d.variants.length === 2 && autoSplit
            ? (i % 2 === 0 ? 'A' : 'B')
            : 'A'
        );
        return { ...d, status: 'idle', active_variant: initialVariant };
      });
      setDrafts(enriched);
      setStage('ready');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Batch draft failed');
      setStage('config');
    }
  };

  const updateDraft = (i, patch) => {
    setDrafts((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  };

  const updateVariant = (i, variantLetter, patch) => {
    setDrafts((prev) => prev.map((d, idx) => {
      if (idx !== i || !d.variants) return d;
      return {
        ...d,
        variants: d.variants.map((v) => (v.variant === variantLetter ? { ...v, ...patch } : v)),
      };
    }));
  };

  const sendOne = async (i) => {
    const d = drafts[i];
    const to = (recipients[d.template_id] || '').trim();
    if (!to.includes('@')) {
      toast.error('Enter the prospect email first');
      return;
    }
    let payload;
    if (d.variants && d.variants.length > 0) {
      const v = d.variants.find((x) => x.variant === d.active_variant) || d.variants[0];
      payload = {
        recipient_email: to,
        subject: v.subject, body_html: v.body_html, body_text: v.body_text,
        draft_hash: v.draft_hash, variant: v.variant, variant_angle: v.angle,
      };
    } else {
      payload = {
        recipient_email: to,
        subject: d.subject, body_html: d.body_html, body_text: d.body_text,
        draft_hash: d.draft_hash,
      };
      if (d.angle) payload.variant_angle = d.angle;
    }

    updateDraft(i, { status: 'sending' });
    try {
      const res = await api.post(
        `/admin/demo-director/templates/${d.template_id}/reengage-send`,
        payload,
      );
      updateDraft(i, {
        status: res.data.sent ? 'sent' : 'send_failed',
        sent_to: res.data.recipient_email,
        sent_variant: res.data.variant,
      });
      if (res.data.sent) {
        toast.success(`Sent ${res.data.variant ? `variant ${res.data.variant} ` : ''}to ${res.data.recipient_email}`);
      } else {
        toast.error('Resend reported failure — audit row written');
      }
    } catch (err) {
      updateDraft(i, { status: 'send_failed' });
      toast.error(err?.response?.data?.detail || 'Send failed');
    }
  };

  if (stage === 'config') {
    return (
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="dd-batch-config">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Button
            size="sm" variant="ghost" onClick={onBack}
            className="h-7 text-[11px] text-slate-600 hover:text-rose-600"
            data-testid="dd-batch-back-btn"
          >
            <ArrowLeft className="h-3 w-3 mr-1" /> Back to leaderboard
          </Button>
          <div className="text-[11px] text-slate-500">
            <Sparkles className="inline h-3 w-3 mr-1 text-rose-600" />
            Batch re-engage · {templateIds.length} templates queued
          </div>
        </div>

        <div className="rounded-lg border border-rose-100 bg-rose-50/40 p-3 text-[12px] text-rose-800">
          <p className="font-semibold mb-1">📨 A/B subject-line tester · batch mode</p>
          <p className="text-rose-700/80">
            Pick two angles, enable auto-split, and we'll alternate A/B across the {templateIds.length} cards
            so each angle gets roughly half. You can override per-card in the next step.
          </p>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer">
              <input
                type="checkbox" checked={abMode}
                onChange={(e) => setAbMode(e.target.checked)}
                className="accent-rose-600"
                data-testid="dd-batch-ab-mode-toggle"
              />
              A/B test two angles
            </label>
            <div className="flex items-center gap-1">
              <AnglePicker
                value={angleA} onChange={onChangeA} angles={angles}
                label={abMode ? 'Variant A' : 'Angle'}
                testId="dd-batch-angle-a-select"
              />
              {!angleATouched && rec?.available && rec.recommended_a?.angle === angleA && (
                <RecommendedPill rec={rec.recommended_a} scope={rec.scope} testId="dd-batch-angle-a-rec-pill" />
              )}
            </div>
            {abMode && (
              <div className="flex items-center gap-1">
                <AnglePicker
                  value={angleB} onChange={onChangeB} angles={angles}
                  label="Variant B"
                  testId="dd-batch-angle-b-select"
                />
                {!angleBTouched && rec?.available && rec.recommended_b?.angle === angleB && (
                  <RecommendedPill rec={rec.recommended_b} scope={rec.scope} testId="dd-batch-angle-b-rec-pill" />
                )}
              </div>
            )}
            {rec?.available && (
              <span className="text-[10px] text-slate-500 ml-auto" data-testid="dd-batch-rec-scope">
                ✨ Auto-recommended from global data
              </span>
            )}
          </div>
          {abMode && (
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer">
              <input
                type="checkbox" checked={autoSplit}
                onChange={(e) => setAutoSplit(e.target.checked)}
                className="accent-rose-600"
                data-testid="dd-batch-auto-split-toggle"
              />
              Auto-split 50/50 (alternate A/B by template order)
              <span className="text-slate-400 font-normal ml-1">— off lets you pick per card</span>
            </label>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            size="sm" onClick={generateBatch}
            className="h-8 gap-1.5 bg-rose-600 text-white hover:bg-rose-700"
            data-testid="dd-batch-generate-btn"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {abMode ? `Generate ${templateIds.length * 2} drafts` : `Generate ${templateIds.length} drafts`}
          </Button>
        </div>
      </div>
    );
  }

  if (stage === 'drafting') {
    return (
      <div className="space-y-3" data-testid="dd-batch-loading">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin text-rose-600" />
          Drafting {abMode ? `${templateIds.length} × 2 variants` : `${templateIds.length} follow-ups`} via GPT-5.2…
        </div>
        <p className="text-[11px] text-slate-400">
          This usually takes 5–15s. Drafts run in parallel.
        </p>
      </div>
    );
  }

  if (!drafts || drafts.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-slate-600" data-testid="dd-batch-empty">
        No drafts were generated.
        <Button size="sm" variant="ghost" onClick={onBack} className="ml-2 text-rose-600">
          <ArrowLeft className="h-3 w-3 mr-1" /> Back
        </Button>
      </div>
    );
  }

  const okCount = drafts.filter((d) => d.ok).length;
  const sentCount = drafts.filter((d) => d.status === 'sent').length;

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="dd-batch-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button
          size="sm" variant="ghost" onClick={onBack}
          className="h-7 text-[11px] text-slate-600 hover:text-rose-600"
          data-testid="dd-batch-back-btn"
        >
          <ArrowLeft className="h-3 w-3 mr-1" /> Back to leaderboard
        </Button>
        <div className="text-[11px] text-slate-500">
          <Sparkles className="inline h-3 w-3 mr-1 text-rose-600" />
          Batch re-engage · {okCount}/{drafts.length} drafts ready · {sentCount} sent
          {abMode && autoSplit && (
            <span className="ml-1 text-violet-600">· auto-split A/B</span>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-rose-100 bg-rose-50/40 p-3 text-[12px] text-rose-800">
        <p className="font-semibold mb-1">📨 Weekly digest follow-up ritual</p>
        <p className="text-rose-700/80">
          Each draft below is anchored on its own template's narrative. Edit subject + body, enter the
          prospect email, click Send. {abMode && 'Click A or B tabs to switch variants per card. '}
          Drafts that aren't sent stay editable so you can return after looking up an email address.
        </p>
      </div>

      <div className="space-y-3">
        {drafts.map((d, i) => {
          const hasVariants = d.variants && d.variants.length > 0;
          const activeVariant = hasVariants
            ? (d.variants.find((v) => v.variant === d.active_variant) || d.variants[0])
            : null;
          const subjectVal = activeVariant ? activeVariant.subject : d.subject;
          const bodyVal = activeVariant ? activeVariant.body_text : d.body_text;
          return (
            <div
              key={d.template_id}
              className={`rounded-lg border p-3 space-y-2 ${
                d.status === 'sent' ? 'border-emerald-200 bg-emerald-50/40'
                : !d.ok ? 'border-amber-200 bg-amber-50/40'
                : 'border-slate-200 bg-white'
              }`}
              data-testid={`dd-batch-card-${d.template_id}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[12px] font-semibold text-slate-800 flex items-center gap-1.5">
                  <span className="text-slate-400 tabular-nums">#{i + 1}</span>
                  {d.template?.name || d.template_id}
                  {d.template?.industry_preset && (
                    <span className="text-[9px] uppercase tracking-wider text-slate-400">
                      {d.template.industry_preset}
                    </span>
                  )}
                </div>
                <div className="text-[10px]">
                  {d.status === 'sent' && (
                    <span className="text-emerald-700 font-semibold">
                      ✓ Sent {d.sent_variant ? `variant ${d.sent_variant} ` : ''}to {d.sent_to}
                    </span>
                  )}
                  {d.status === 'send_failed' && (
                    <span className="text-rose-700 font-semibold">✗ Send failed (audit logged)</span>
                  )}
                </div>
              </div>

              {!d.ok && (
                <p className="text-[11px] text-amber-800" data-testid={`dd-batch-card-${d.template_id}-error`}>
                  ⚠️ Skipped — {d.error}
                </p>
              )}

              {d.ok && d.status !== 'sent' && (
                <>
                  {hasVariants && d.variants.length === 2 && (
                    <div className="flex items-center gap-1" data-testid={`dd-batch-card-${d.template_id}-variant-tabs`}>
                      {d.variants.map((v) => (
                        <button
                          key={v.variant} type="button"
                          onClick={() => updateDraft(i, { active_variant: v.variant })}
                          className={`text-[10px] font-semibold rounded-full ring-1 py-0.5 px-2 transition ${
                            d.active_variant === v.variant
                              ? variantBadgeCls(v.variant)
                              : 'bg-white text-slate-500 ring-slate-200 hover:ring-slate-300'
                          }`}
                          data-testid={`dd-batch-card-${d.template_id}-tab-${v.variant}`}
                        >
                          {v.variant} · {v.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <Input
                    value={subjectVal || ''}
                    onChange={(e) => (
                      hasVariants
                        ? updateVariant(i, d.active_variant, { subject: e.target.value })
                        : updateDraft(i, { subject: e.target.value })
                    )}
                    className="text-[12px] font-medium"
                    placeholder="Subject"
                    data-testid={`dd-batch-card-${d.template_id}-subject`}
                  />
                  <Textarea
                    value={bodyVal || ''}
                    onChange={(e) => {
                      const newText = e.target.value;
                      const html = newText.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
                      if (hasVariants) {
                        updateVariant(i, d.active_variant, { body_text: newText, body_html: html });
                      } else {
                        updateDraft(i, { body_text: newText, body_html: html });
                      }
                    }}
                    rows={5}
                    className="text-[11px] font-mono"
                    data-testid={`dd-batch-card-${d.template_id}-body`}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="email"
                      value={recipients[d.template_id] || ''}
                      onChange={(e) => setRecipients({ ...recipients, [d.template_id]: e.target.value })}
                      className="text-[12px] flex-1 min-w-[180px]"
                      placeholder="prospect@company.com"
                      data-testid={`dd-batch-card-${d.template_id}-recipient`}
                    />
                    <Button
                      size="sm" onClick={() => sendOne(i)}
                      disabled={d.status === 'sending'}
                      className="h-8 text-[11px] gap-1 bg-rose-600 text-white hover:bg-rose-700"
                      data-testid={`dd-batch-card-${d.template_id}-send-btn`}
                    >
                      {d.status === 'sending' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      Send {hasVariants ? d.active_variant : ''}
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
